import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { $getRoot } from "lexical";
import { type FC, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type UUID, uuidv4 } from "../../../../../utils/uuid";
import type { MomentFormMoment } from "../../../../moment/types/moment-form-moment";
import { useSegmentsWithFavourites } from "../../../../segment/server/use-segments";
import { useLexicalContext } from "../../lexical-context";
import { $isDynamicContentNode } from "../../nodes/dynamic-content/dynamic-content-node";
import { EDIT_DYNAMIC_CONTENT_COMMAND, INSERT_DYNAMIC_CONTENT_COMMAND } from "../../types/commands";
import { $findAllNodesOfType } from "../../utils/find-all-nodes-of-type";
import { useDynamicContentButtonCommands } from "../toolbar/dynamic-content-button/hooks/use-dynamic-content-button-commands";
import { SegmentSelect } from "./segment-select";

export const DynamicContentPlugin: FC = () => {
  const { t } = useTranslation();
  const [segmentId, setSegmentid] = useState<UUID | null>(null);
  const [editor] = useLexicalComposerContext();
  const { setValue } = useFormContext<MomentFormMoment>();

  useDynamicContentButtonCommands();

  const segmentLoader = useSegmentsWithFavourites({
    include_deleted: false,
    is_recipient_type: false,
  });

  const {
    editorModal: { open, handleClose, data },
  } = useLexicalContext();

  const onClick = useCallback(
    (id: UUID | null) => {
      setSegmentid(id);
    },
    [setSegmentid],
  );

  const onSubmit = useCallback(
    (id: UUID) => {
      if (!data || data.type !== "dynamic-content") return;

      data?.data.mode === "edit"
        ? editor.dispatchCommand(EDIT_DYNAMIC_CONTENT_COMMAND, {
            key: data.data.key,
            segment_id: id,
          })
        : editor.dispatchCommand(INSERT_DYNAMIC_CONTENT_COMMAND, {
            segment_id: id,
          });

      editor.read(() => {
        const nodes = $findAllNodesOfType($getRoot(), $isDynamicContentNode);
        setValue(
          "dynamic_segment_id_map",
          nodes.reduce(
            (acc, node) => {
              acc[node.getSegmentId()] = node.getCleanSegmentId();
              return acc;
            },
            {} as Record<string, string>,
          ),
        );
      });

      handleClose();
    },
    [editor, handleClose, setValue, data],
  );

  useEffect(() => {
    data?.type === "dynamic-content" &&
      data.data.mode === "edit" &&
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSegmentid(data.data.segmentId);
  }, [data]);

  if (!data || data.type !== "dynamic-content") return null;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t("Select a segment to target")}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "Only people in the recipient segment and the selected targeting segment will see this content.",
            )}
          </Typography>
          <SegmentSelect
            segmentLoader={segmentLoader}
            onChange={onClick}
            segmentInfo={{
              selected: segmentId,
              createNew: uuidv4(),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" data-analytics-id="cancel-dynamic-content">
          {t("Cancel")}
        </Button>
        <Button
          onClick={segmentId ? () => onSubmit(segmentId) : undefined}
          color="primary"
          disabled={!segmentId}
          data-analytics-id="insert-dynamic-content"
        >
          {data.data.mode === "add" ? t("Insert") : t("Update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
